/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  CreateCommentThreadPathParameters,
  CreateCommentThreadReplyPathParameters,
  DeleteCommentThreadCommentPathParameters,
  ListCommentThreadCommentsParams,
  ListCommentThreadCommentsPathParameters,
  ListCommentThreadsParams,
  ListCommentThreadsPathParameters,
  ReopenCommentThreadPathParameters,
  ResolveCommentThreadPathParameters,
  TLHTTPValidationError,
  TLNexusCommentBodyRequest,
  TLNexusCommentResponse,
  TLNexusCommentThreadCreateRequest,
  TLNexusCommentThreadResponse,
  TLPaginatedResponseNexusCommentResponse,
  TLPaginatedResponseNexusCommentThreadResponse,
  UpdateCommentThreadCommentPathParameters,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary List Comment Threads
 */
export const listCommentThreads = (
  { projectId }: ListCommentThreadsPathParameters,
  params?: ListCommentThreadsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPaginatedResponseNexusCommentThreadResponse>(
    { url: `/nexus/projects/${projectId}/comment-threads`, method: 'GET', params, signal },
    options,
  );
};

export const getListCommentThreadsQueryKey = (
  { projectId }: ListCommentThreadsPathParameters,
  params?: ListCommentThreadsParams,
) => {
  return ['nexus', 'projects', projectId, 'comment-threads', ...(params ? [params] : [])] as const;
};

export const getListCommentThreadsQueryOptions = <
  TData = Awaited<ReturnType<typeof listCommentThreads>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: ListCommentThreadsPathParameters,
  params?: ListCommentThreadsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCommentThreads>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListCommentThreadsQueryKey({ projectId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listCommentThreads>>> = ({ signal }) =>
    listCommentThreads({ projectId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof listCommentThreads>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ListCommentThreadsQueryResult = NonNullable<Awaited<ReturnType<typeof listCommentThreads>>>;
export type ListCommentThreadsQueryError = ErrorType<TLHTTPValidationError>;

export function useListCommentThreads<
  TData = Awaited<ReturnType<typeof listCommentThreads>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListCommentThreadsPathParameters,
  params: undefined | ListCommentThreadsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCommentThreads>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listCommentThreads>>,
          TError,
          Awaited<ReturnType<typeof listCommentThreads>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListCommentThreads<
  TData = Awaited<ReturnType<typeof listCommentThreads>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListCommentThreadsPathParameters,
  params?: ListCommentThreadsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCommentThreads>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listCommentThreads>>,
          TError,
          Awaited<ReturnType<typeof listCommentThreads>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListCommentThreads<
  TData = Awaited<ReturnType<typeof listCommentThreads>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListCommentThreadsPathParameters,
  params?: ListCommentThreadsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCommentThreads>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Comment Threads
 */

export function useListCommentThreads<
  TData = Awaited<ReturnType<typeof listCommentThreads>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: ListCommentThreadsPathParameters,
  params?: ListCommentThreadsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCommentThreads>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListCommentThreadsQueryOptions({ projectId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Comment Threads
 */
export const prefetchListCommentThreadsQuery = async <
  TData = Awaited<ReturnType<typeof listCommentThreads>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId }: ListCommentThreadsPathParameters,
  params?: ListCommentThreadsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCommentThreads>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListCommentThreadsQueryOptions({ projectId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Comment Threads
 */
export const invalidateListCommentThreads = async (
  queryClient: QueryClient,
  { projectId }: ListCommentThreadsPathParameters,
  params?: ListCommentThreadsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getListCommentThreadsQueryKey({ projectId }, params) }, options);

  return queryClient;
};

/**
 * @summary Create Comment Thread
 */
export const createCommentThread = (
  { projectId }: CreateCommentThreadPathParameters,
  tLNexusCommentThreadCreateRequest: BodyType<TLNexusCommentThreadCreateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusCommentThreadResponse>(
    {
      url: `/nexus/projects/${projectId}/comment-threads`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLNexusCommentThreadCreateRequest,
      signal,
    },
    options,
  );
};

export const getCreateCommentThreadQueryKey = (
  { projectId }: CreateCommentThreadPathParameters,
  tLNexusCommentThreadCreateRequest?: BodyType<TLNexusCommentThreadCreateRequest>,
) => {
  return ['POST', 'nexus', 'projects', projectId, 'comment-threads', tLNexusCommentThreadCreateRequest] as const;
};

export const getCreateCommentThreadQueryOptions = <
  TData = Awaited<ReturnType<typeof createCommentThread>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: CreateCommentThreadPathParameters,
  tLNexusCommentThreadCreateRequest: BodyType<TLNexusCommentThreadCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCommentThread>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getCreateCommentThreadQueryKey({ projectId }, tLNexusCommentThreadCreateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createCommentThread>>> = ({ signal }) =>
    createCommentThread({ projectId }, tLNexusCommentThreadCreateRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof createCommentThread>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type CreateCommentThreadQueryResult = NonNullable<Awaited<ReturnType<typeof createCommentThread>>>;
export type CreateCommentThreadQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateCommentThread<
  TData = Awaited<ReturnType<typeof createCommentThread>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateCommentThreadPathParameters,
  tLNexusCommentThreadCreateRequest: BodyType<TLNexusCommentThreadCreateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCommentThread>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createCommentThread>>,
          TError,
          Awaited<ReturnType<typeof createCommentThread>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateCommentThread<
  TData = Awaited<ReturnType<typeof createCommentThread>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateCommentThreadPathParameters,
  tLNexusCommentThreadCreateRequest: BodyType<TLNexusCommentThreadCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCommentThread>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createCommentThread>>,
          TError,
          Awaited<ReturnType<typeof createCommentThread>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateCommentThread<
  TData = Awaited<ReturnType<typeof createCommentThread>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateCommentThreadPathParameters,
  tLNexusCommentThreadCreateRequest: BodyType<TLNexusCommentThreadCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCommentThread>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Comment Thread
 */

export function useCreateCommentThread<
  TData = Awaited<ReturnType<typeof createCommentThread>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId }: CreateCommentThreadPathParameters,
  tLNexusCommentThreadCreateRequest: BodyType<TLNexusCommentThreadCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCommentThread>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateCommentThreadQueryOptions({ projectId }, tLNexusCommentThreadCreateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Comment Thread
 */
export const prefetchCreateCommentThreadQuery = async <
  TData = Awaited<ReturnType<typeof createCommentThread>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId }: CreateCommentThreadPathParameters,
  tLNexusCommentThreadCreateRequest: BodyType<TLNexusCommentThreadCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCommentThread>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateCommentThreadQueryOptions({ projectId }, tLNexusCommentThreadCreateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Comment Thread
 */
export const invalidateCreateCommentThread = async (
  queryClient: QueryClient,
  { projectId }: CreateCommentThreadPathParameters,
  tLNexusCommentThreadCreateRequest: BodyType<TLNexusCommentThreadCreateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getCreateCommentThreadQueryKey({ projectId }, tLNexusCommentThreadCreateRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary List Comment Thread Comments
 */
export const listCommentThreadComments = (
  { projectId, threadId }: ListCommentThreadCommentsPathParameters,
  params?: ListCommentThreadCommentsParams,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLPaginatedResponseNexusCommentResponse>(
    { url: `/nexus/projects/${projectId}/comment-threads/${threadId}/comments`, method: 'GET', params, signal },
    options,
  );
};

export const getListCommentThreadCommentsQueryKey = (
  { projectId, threadId }: ListCommentThreadCommentsPathParameters,
  params?: ListCommentThreadCommentsParams,
) => {
  return [
    'nexus',
    'projects',
    projectId,
    'comment-threads',
    threadId,
    'comments',
    ...(params ? [params] : []),
  ] as const;
};

export const getListCommentThreadCommentsQueryOptions = <
  TData = Awaited<ReturnType<typeof listCommentThreadComments>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, threadId }: ListCommentThreadCommentsPathParameters,
  params?: ListCommentThreadCommentsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCommentThreadComments>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getListCommentThreadCommentsQueryKey({ projectId, threadId }, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof listCommentThreadComments>>> = ({ signal }) =>
    listCommentThreadComments({ projectId, threadId }, params, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined && threadId !== null && threadId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof listCommentThreadComments>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ListCommentThreadCommentsQueryResult = NonNullable<Awaited<ReturnType<typeof listCommentThreadComments>>>;
export type ListCommentThreadCommentsQueryError = ErrorType<TLHTTPValidationError>;

export function useListCommentThreadComments<
  TData = Awaited<ReturnType<typeof listCommentThreadComments>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListCommentThreadCommentsPathParameters,
  params: undefined | ListCommentThreadCommentsParams,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCommentThreadComments>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listCommentThreadComments>>,
          TError,
          Awaited<ReturnType<typeof listCommentThreadComments>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListCommentThreadComments<
  TData = Awaited<ReturnType<typeof listCommentThreadComments>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListCommentThreadCommentsPathParameters,
  params?: ListCommentThreadCommentsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCommentThreadComments>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listCommentThreadComments>>,
          TError,
          Awaited<ReturnType<typeof listCommentThreadComments>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useListCommentThreadComments<
  TData = Awaited<ReturnType<typeof listCommentThreadComments>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ListCommentThreadCommentsPathParameters,
  params?: ListCommentThreadCommentsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCommentThreadComments>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary List Comment Thread Comments
 */

export function useListCommentThreadComments<
  TData = Awaited<ReturnType<typeof listCommentThreadComments>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, threadId }: ListCommentThreadCommentsPathParameters,
  params?: ListCommentThreadCommentsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCommentThreadComments>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getListCommentThreadCommentsQueryOptions({ projectId, threadId }, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary List Comment Thread Comments
 */
export const prefetchListCommentThreadCommentsQuery = async <
  TData = Awaited<ReturnType<typeof listCommentThreadComments>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId, threadId }: ListCommentThreadCommentsPathParameters,
  params?: ListCommentThreadCommentsParams,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof listCommentThreadComments>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getListCommentThreadCommentsQueryOptions({ projectId, threadId }, params, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary List Comment Thread Comments
 */
export const invalidateListCommentThreadComments = async (
  queryClient: QueryClient,
  { projectId, threadId }: ListCommentThreadCommentsPathParameters,
  params?: ListCommentThreadCommentsParams,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getListCommentThreadCommentsQueryKey({ projectId, threadId }, params) },
    options,
  );

  return queryClient;
};

/**
 * @summary Create Comment Thread Reply
 */
export const createCommentThreadReply = (
  { projectId, threadId }: CreateCommentThreadReplyPathParameters,
  tLNexusCommentBodyRequest: BodyType<TLNexusCommentBodyRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusCommentResponse>(
    {
      url: `/nexus/projects/${projectId}/comment-threads/${threadId}/comments`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLNexusCommentBodyRequest,
      signal,
    },
    options,
  );
};

export const getCreateCommentThreadReplyQueryKey = (
  { projectId, threadId }: CreateCommentThreadReplyPathParameters,
  tLNexusCommentBodyRequest?: BodyType<TLNexusCommentBodyRequest>,
) => {
  return [
    'POST',
    'nexus',
    'projects',
    projectId,
    'comment-threads',
    threadId,
    'comments',
    tLNexusCommentBodyRequest,
  ] as const;
};

export const getCreateCommentThreadReplyQueryOptions = <
  TData = Awaited<ReturnType<typeof createCommentThreadReply>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, threadId }: CreateCommentThreadReplyPathParameters,
  tLNexusCommentBodyRequest: BodyType<TLNexusCommentBodyRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCommentThreadReply>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getCreateCommentThreadReplyQueryKey({ projectId, threadId }, tLNexusCommentBodyRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createCommentThreadReply>>> = ({ signal }) =>
    createCommentThreadReply({ projectId, threadId }, tLNexusCommentBodyRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined && threadId !== null && threadId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof createCommentThreadReply>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type CreateCommentThreadReplyQueryResult = NonNullable<Awaited<ReturnType<typeof createCommentThreadReply>>>;
export type CreateCommentThreadReplyQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateCommentThreadReply<
  TData = Awaited<ReturnType<typeof createCommentThreadReply>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateCommentThreadReplyPathParameters,
  tLNexusCommentBodyRequest: BodyType<TLNexusCommentBodyRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCommentThreadReply>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createCommentThreadReply>>,
          TError,
          Awaited<ReturnType<typeof createCommentThreadReply>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateCommentThreadReply<
  TData = Awaited<ReturnType<typeof createCommentThreadReply>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateCommentThreadReplyPathParameters,
  tLNexusCommentBodyRequest: BodyType<TLNexusCommentBodyRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCommentThreadReply>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createCommentThreadReply>>,
          TError,
          Awaited<ReturnType<typeof createCommentThreadReply>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateCommentThreadReply<
  TData = Awaited<ReturnType<typeof createCommentThreadReply>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: CreateCommentThreadReplyPathParameters,
  tLNexusCommentBodyRequest: BodyType<TLNexusCommentBodyRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCommentThreadReply>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Comment Thread Reply
 */

export function useCreateCommentThreadReply<
  TData = Awaited<ReturnType<typeof createCommentThreadReply>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, threadId }: CreateCommentThreadReplyPathParameters,
  tLNexusCommentBodyRequest: BodyType<TLNexusCommentBodyRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCommentThreadReply>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateCommentThreadReplyQueryOptions(
    { projectId, threadId },
    tLNexusCommentBodyRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Comment Thread Reply
 */
export const prefetchCreateCommentThreadReplyQuery = async <
  TData = Awaited<ReturnType<typeof createCommentThreadReply>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId, threadId }: CreateCommentThreadReplyPathParameters,
  tLNexusCommentBodyRequest: BodyType<TLNexusCommentBodyRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCommentThreadReply>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateCommentThreadReplyQueryOptions(
    { projectId, threadId },
    tLNexusCommentBodyRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Comment Thread Reply
 */
export const invalidateCreateCommentThreadReply = async (
  queryClient: QueryClient,
  { projectId, threadId }: CreateCommentThreadReplyPathParameters,
  tLNexusCommentBodyRequest: BodyType<TLNexusCommentBodyRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getCreateCommentThreadReplyQueryKey({ projectId, threadId }, tLNexusCommentBodyRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Delete Comment Thread Comment
 */
export const deleteCommentThreadComment = (
  { projectId, threadId, commentId }: DeleteCommentThreadCommentPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<void>(
    { url: `/nexus/projects/${projectId}/comment-threads/${threadId}/comments/${commentId}`, method: 'DELETE', signal },
    options,
  );
};

export const getDeleteCommentThreadCommentQueryKey = ({
  projectId,
  threadId,
  commentId,
}: DeleteCommentThreadCommentPathParameters) => {
  return ['DELETE', 'nexus', 'projects', projectId, 'comment-threads', threadId, 'comments', commentId] as const;
};

export const getDeleteCommentThreadCommentQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteCommentThreadComment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, threadId, commentId }: DeleteCommentThreadCommentPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCommentThreadComment>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteCommentThreadCommentQueryKey({ projectId, threadId, commentId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteCommentThreadComment>>> = ({ signal }) =>
    deleteCommentThreadComment({ projectId, threadId, commentId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled:
      projectId !== null &&
      projectId !== undefined &&
      threadId !== null &&
      threadId !== undefined &&
      commentId !== null &&
      commentId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof deleteCommentThreadComment>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type DeleteCommentThreadCommentQueryResult = NonNullable<Awaited<ReturnType<typeof deleteCommentThreadComment>>>;
export type DeleteCommentThreadCommentQueryError = ErrorType<TLHTTPValidationError>;

export function useDeleteCommentThreadComment<
  TData = Awaited<ReturnType<typeof deleteCommentThreadComment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteCommentThreadCommentPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCommentThreadComment>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteCommentThreadComment>>,
          TError,
          Awaited<ReturnType<typeof deleteCommentThreadComment>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteCommentThreadComment<
  TData = Awaited<ReturnType<typeof deleteCommentThreadComment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteCommentThreadCommentPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCommentThreadComment>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteCommentThreadComment>>,
          TError,
          Awaited<ReturnType<typeof deleteCommentThreadComment>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteCommentThreadComment<
  TData = Awaited<ReturnType<typeof deleteCommentThreadComment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteCommentThreadCommentPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCommentThreadComment>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Comment Thread Comment
 */

export function useDeleteCommentThreadComment<
  TData = Awaited<ReturnType<typeof deleteCommentThreadComment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, threadId, commentId }: DeleteCommentThreadCommentPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCommentThreadComment>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteCommentThreadCommentQueryOptions({ projectId, threadId, commentId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Comment Thread Comment
 */
export const prefetchDeleteCommentThreadCommentQuery = async <
  TData = Awaited<ReturnType<typeof deleteCommentThreadComment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId, threadId, commentId }: DeleteCommentThreadCommentPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCommentThreadComment>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteCommentThreadCommentQueryOptions({ projectId, threadId, commentId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Comment Thread Comment
 */
export const invalidateDeleteCommentThreadComment = async (
  queryClient: QueryClient,
  { projectId, threadId, commentId }: DeleteCommentThreadCommentPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getDeleteCommentThreadCommentQueryKey({ projectId, threadId, commentId }) },
    options,
  );

  return queryClient;
};

/**
 * @summary Update Comment Thread Comment
 */
export const updateCommentThreadComment = (
  { projectId, threadId, commentId }: UpdateCommentThreadCommentPathParameters,
  tLNexusCommentBodyRequest: BodyType<TLNexusCommentBodyRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusCommentResponse>(
    {
      url: `/nexus/projects/${projectId}/comment-threads/${threadId}/comments/${commentId}`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: tLNexusCommentBodyRequest,
      signal,
    },
    options,
  );
};

export const getUpdateCommentThreadCommentQueryKey = (
  { projectId, threadId, commentId }: UpdateCommentThreadCommentPathParameters,
  tLNexusCommentBodyRequest?: BodyType<TLNexusCommentBodyRequest>,
) => {
  return [
    'PATCH',
    'nexus',
    'projects',
    projectId,
    'comment-threads',
    threadId,
    'comments',
    commentId,
    tLNexusCommentBodyRequest,
  ] as const;
};

export const getUpdateCommentThreadCommentQueryOptions = <
  TData = Awaited<ReturnType<typeof updateCommentThreadComment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, threadId, commentId }: UpdateCommentThreadCommentPathParameters,
  tLNexusCommentBodyRequest: BodyType<TLNexusCommentBodyRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateCommentThreadComment>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getUpdateCommentThreadCommentQueryKey({ projectId, threadId, commentId }, tLNexusCommentBodyRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateCommentThreadComment>>> = ({ signal }) =>
    updateCommentThreadComment({ projectId, threadId, commentId }, tLNexusCommentBodyRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled:
      projectId !== null &&
      projectId !== undefined &&
      threadId !== null &&
      threadId !== undefined &&
      commentId !== null &&
      commentId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof updateCommentThreadComment>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type UpdateCommentThreadCommentQueryResult = NonNullable<Awaited<ReturnType<typeof updateCommentThreadComment>>>;
export type UpdateCommentThreadCommentQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateCommentThreadComment<
  TData = Awaited<ReturnType<typeof updateCommentThreadComment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateCommentThreadCommentPathParameters,
  tLNexusCommentBodyRequest: BodyType<TLNexusCommentBodyRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateCommentThreadComment>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateCommentThreadComment>>,
          TError,
          Awaited<ReturnType<typeof updateCommentThreadComment>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateCommentThreadComment<
  TData = Awaited<ReturnType<typeof updateCommentThreadComment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateCommentThreadCommentPathParameters,
  tLNexusCommentBodyRequest: BodyType<TLNexusCommentBodyRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateCommentThreadComment>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateCommentThreadComment>>,
          TError,
          Awaited<ReturnType<typeof updateCommentThreadComment>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateCommentThreadComment<
  TData = Awaited<ReturnType<typeof updateCommentThreadComment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateCommentThreadCommentPathParameters,
  tLNexusCommentBodyRequest: BodyType<TLNexusCommentBodyRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateCommentThreadComment>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Comment Thread Comment
 */

export function useUpdateCommentThreadComment<
  TData = Awaited<ReturnType<typeof updateCommentThreadComment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, threadId, commentId }: UpdateCommentThreadCommentPathParameters,
  tLNexusCommentBodyRequest: BodyType<TLNexusCommentBodyRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateCommentThreadComment>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateCommentThreadCommentQueryOptions(
    { projectId, threadId, commentId },
    tLNexusCommentBodyRequest,
    options,
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Comment Thread Comment
 */
export const prefetchUpdateCommentThreadCommentQuery = async <
  TData = Awaited<ReturnType<typeof updateCommentThreadComment>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId, threadId, commentId }: UpdateCommentThreadCommentPathParameters,
  tLNexusCommentBodyRequest: BodyType<TLNexusCommentBodyRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateCommentThreadComment>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateCommentThreadCommentQueryOptions(
    { projectId, threadId, commentId },
    tLNexusCommentBodyRequest,
    options,
  );

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Comment Thread Comment
 */
export const invalidateUpdateCommentThreadComment = async (
  queryClient: QueryClient,
  { projectId, threadId, commentId }: UpdateCommentThreadCommentPathParameters,
  tLNexusCommentBodyRequest: BodyType<TLNexusCommentBodyRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateCommentThreadCommentQueryKey({ projectId, threadId, commentId }, tLNexusCommentBodyRequest) },
    options,
  );

  return queryClient;
};

/**
 * @summary Reopen Comment Thread
 */
export const reopenCommentThread = (
  { projectId, threadId }: ReopenCommentThreadPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusCommentThreadResponse>(
    { url: `/nexus/projects/${projectId}/comment-threads/${threadId}/reopen`, method: 'POST', signal },
    options,
  );
};

export const getReopenCommentThreadQueryKey = ({ projectId, threadId }: ReopenCommentThreadPathParameters) => {
  return ['POST', 'nexus', 'projects', projectId, 'comment-threads', threadId, 'reopen'] as const;
};

export const getReopenCommentThreadQueryOptions = <
  TData = Awaited<ReturnType<typeof reopenCommentThread>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, threadId }: ReopenCommentThreadPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof reopenCommentThread>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getReopenCommentThreadQueryKey({ projectId, threadId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof reopenCommentThread>>> = ({ signal }) =>
    reopenCommentThread({ projectId, threadId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined && threadId !== null && threadId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof reopenCommentThread>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ReopenCommentThreadQueryResult = NonNullable<Awaited<ReturnType<typeof reopenCommentThread>>>;
export type ReopenCommentThreadQueryError = ErrorType<TLHTTPValidationError>;

export function useReopenCommentThread<
  TData = Awaited<ReturnType<typeof reopenCommentThread>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ReopenCommentThreadPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof reopenCommentThread>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof reopenCommentThread>>,
          TError,
          Awaited<ReturnType<typeof reopenCommentThread>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useReopenCommentThread<
  TData = Awaited<ReturnType<typeof reopenCommentThread>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ReopenCommentThreadPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof reopenCommentThread>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof reopenCommentThread>>,
          TError,
          Awaited<ReturnType<typeof reopenCommentThread>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useReopenCommentThread<
  TData = Awaited<ReturnType<typeof reopenCommentThread>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ReopenCommentThreadPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof reopenCommentThread>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Reopen Comment Thread
 */

export function useReopenCommentThread<
  TData = Awaited<ReturnType<typeof reopenCommentThread>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, threadId }: ReopenCommentThreadPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof reopenCommentThread>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getReopenCommentThreadQueryOptions({ projectId, threadId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Reopen Comment Thread
 */
export const prefetchReopenCommentThreadQuery = async <
  TData = Awaited<ReturnType<typeof reopenCommentThread>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId, threadId }: ReopenCommentThreadPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof reopenCommentThread>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getReopenCommentThreadQueryOptions({ projectId, threadId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Reopen Comment Thread
 */
export const invalidateReopenCommentThread = async (
  queryClient: QueryClient,
  { projectId, threadId }: ReopenCommentThreadPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getReopenCommentThreadQueryKey({ projectId, threadId }) }, options);

  return queryClient;
};

/**
 * @summary Resolve Comment Thread
 */
export const resolveCommentThread = (
  { projectId, threadId }: ResolveCommentThreadPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLNexusCommentThreadResponse>(
    { url: `/nexus/projects/${projectId}/comment-threads/${threadId}/resolve`, method: 'POST', signal },
    options,
  );
};

export const getResolveCommentThreadQueryKey = ({ projectId, threadId }: ResolveCommentThreadPathParameters) => {
  return ['POST', 'nexus', 'projects', projectId, 'comment-threads', threadId, 'resolve'] as const;
};

export const getResolveCommentThreadQueryOptions = <
  TData = Awaited<ReturnType<typeof resolveCommentThread>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, threadId }: ResolveCommentThreadPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof resolveCommentThread>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getResolveCommentThreadQueryKey({ projectId, threadId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof resolveCommentThread>>> = ({ signal }) =>
    resolveCommentThread({ projectId, threadId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: projectId !== null && projectId !== undefined && threadId !== null && threadId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof resolveCommentThread>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type ResolveCommentThreadQueryResult = NonNullable<Awaited<ReturnType<typeof resolveCommentThread>>>;
export type ResolveCommentThreadQueryError = ErrorType<TLHTTPValidationError>;

export function useResolveCommentThread<
  TData = Awaited<ReturnType<typeof resolveCommentThread>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ResolveCommentThreadPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof resolveCommentThread>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof resolveCommentThread>>,
          TError,
          Awaited<ReturnType<typeof resolveCommentThread>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useResolveCommentThread<
  TData = Awaited<ReturnType<typeof resolveCommentThread>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ResolveCommentThreadPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof resolveCommentThread>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof resolveCommentThread>>,
          TError,
          Awaited<ReturnType<typeof resolveCommentThread>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useResolveCommentThread<
  TData = Awaited<ReturnType<typeof resolveCommentThread>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: ResolveCommentThreadPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof resolveCommentThread>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Resolve Comment Thread
 */

export function useResolveCommentThread<
  TData = Awaited<ReturnType<typeof resolveCommentThread>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { projectId, threadId }: ResolveCommentThreadPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof resolveCommentThread>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getResolveCommentThreadQueryOptions({ projectId, threadId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Resolve Comment Thread
 */
export const prefetchResolveCommentThreadQuery = async <
  TData = Awaited<ReturnType<typeof resolveCommentThread>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { projectId, threadId }: ResolveCommentThreadPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof resolveCommentThread>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getResolveCommentThreadQueryOptions({ projectId, threadId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Resolve Comment Thread
 */
export const invalidateResolveCommentThread = async (
  queryClient: QueryClient,
  { projectId, threadId }: ResolveCommentThreadPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getResolveCommentThreadQueryKey({ projectId, threadId }) }, options);

  return queryClient;
};
